import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';

import { Button } from './Button';
import { Icon } from './Icon';
import { useFieldWiring } from './useFieldWiring';
import { todayInZone } from './zonedTime';

/**
 * Dates in this product are calendar dates, not instants.
 *
 * A job is scheduled on the 3rd; it is the 3rd in the branch's own town no
 * matter where the browser is. So everything here is computed in UTC and
 * exchanged as an ISO `YYYY-MM-DD` string, which keeps a picker in Chicago and
 * a backend in UTC from ever disagreeing by a day.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Formats a UTC date as `YYYY-MM-DD`. */
export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Parses `YYYY-MM-DD` into a UTC date, or null when it is not a real date. */
export function fromIsoDate(value: string): Date | null {
  if (!ISO_DATE.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) || toIsoDate(parsed) !== value ? null : parsed;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
  next.setUTCDate(Math.min(date.getUTCDate(), lastDay));
  return next;
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
// Composed from two formatters rather than one. A single formatter with
// `weekday` included emits a locale separator ("Thursday, 17 September 2026"),
// and a comma inside an accessible name is read aloud as a pause that makes the
// date sound like two separate values.
const WEEKDAY_LABEL = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' });
const FULL_DATE_LABEL = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** The accessible name of one day cell, e.g. "Thursday 17 September 2026". */
function dayLabel(date: Date): string {
  return `${WEEKDAY_LABEL.format(date)} ${FULL_DATE_LABEL.format(date)}`;
}

/** Monday-first, because a working week in the trades starts on Monday. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The six-week grid containing the given month, Monday-first. */
function monthGrid(anchor: Date): Date[][] {
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const start = addDays(first, -offset);

  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  );
}

export interface DatePickerProps {
  /** The selected date as `YYYY-MM-DD`, or null. */
  readonly value: string | null;
  /** Called with the new ISO date, or null when the field is cleared. */
  readonly onChange: (value: string | null) => void;
  /** Accessible name, required when used outside a FormField. */
  readonly 'aria-label'?: string;
  /**
   * Whether the field is read-only.
   *
   * Disables the text input and the calendar trigger together. A disabled input
   * beside a live calendar button is the worst of both: the date still changes,
   * just by a route the caller did not think they had left open.
   */
  readonly disabled?: boolean;
  /**
   * The IANA zone whose calendar decides which day is "today" when nothing is
   * selected. Defaults to the browser's own zone.
   */
  readonly timeZone?: string;
}

/**
 * Today on the given zone's wall calendar, as a UTC date. Not `new Date()`:
 * that is the UTC day, which in Chicago turns into tomorrow at 7 pm.
 */
function today(timeZone: string | undefined): Date {
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return fromIsoDate(todayInZone(zone)) ?? new Date();
}

/**
 * A date field with a keyboard-operable calendar.
 *
 * The text input is the primary control and is always usable on its own: a
 * dispatcher who knows the date types it and never opens the calendar. The
 * calendar is a `dialog` containing a `grid`, navigated with the arrow keys,
 * PageUp/PageDown for months and Home/End for the ends of a week — the ARIA
 * date-picker pattern, not a mouse-only popup.
 *
 * `type="text"` rather than `type="date"`: the native control's appearance and
 * keyboard behaviour differ per browser and cannot be brought into a design
 * system, and its segmented editing fights a typed ISO date.
 *
 * @param props see {@link DatePickerProps}
 * @returns the date field
 */
export function DatePicker({
  value,
  onChange,
  'aria-label': ariaLabel,
  disabled = false,
  timeZone,
}: DatePickerProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();
  const dialogId = `${controlId}-calendar`;

  const selected = value === null ? null : fromIsoDate(value);
  const [open, setOpen] = useState(false);
  const [focusedDate, setFocusedDate] = useState<Date>(() => selected ?? today(timeZone));
  const gridRef = useRef<HTMLTableElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Moving the browser's focus to the day cell is what makes the grid usable:
  // without it the arrow keys would still be going to the text input.
  useEffect(() => {
    if (!open) {
      return;
    }
    const cell = gridRef.current?.querySelector<HTMLButtonElement>('[data-focused]');
    cell?.focus();
  }, [open, focusedDate]);

  // Dismissal on an outside click is bound to pointer-down rather than to the
  // dialog's blur. Blur is the wrong signal here: focus legitimately moves
  // between the month buttons and forty-two day cells while the calendar is
  // open, and a blur-driven close turns every arrow key into a coin flip.
  useEffect(() => {
    if (!open) {
      return;
    }
    function handlePointerDown(event: MouseEvent): void {
      const root = dialogRef.current;
      const target = event.target;
      if (root !== null && target instanceof Node && !root.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
    };
  }, [open]);

  function close(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }

  function choose(date: Date): void {
    onChange(toIsoDate(date));
    close(true);
  }

  function handleGridKeyDown(event: KeyboardEvent<HTMLTableElement>): void {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    const step = moves[event.key];
    if (step !== undefined) {
      event.preventDefault();
      setFocusedDate((current) => addDays(current, step));
      return;
    }

    switch (event.key) {
      case 'Home':
        event.preventDefault();
        setFocusedDate((current) => addDays(current, -((current.getUTCDay() + 6) % 7)));
        return;
      case 'End':
        event.preventDefault();
        setFocusedDate((current) => addDays(current, 6 - ((current.getUTCDay() + 6) % 7)));
        return;
      case 'PageUp':
        event.preventDefault();
        setFocusedDate((current) => addMonths(current, event.shiftKey ? -12 : -1));
        return;
      case 'PageDown':
        event.preventDefault();
        setFocusedDate((current) => addMonths(current, event.shiftKey ? 12 : 1));
        return;
      default:
    }
  }

  // On the whole dialog, not only the grid: Escape from the month buttons must
  // close the calendar too, and must not reach a Modal the picker sits in.
  function handleDialogKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
  }

  // Tabbing out leaves the calendar behind. Only a focus move to a known
  // element outside counts: a day cell unmounting as the month changes blurs
  // with no relatedTarget, and that is not the user leaving.
  function handleDialogBlur(event: FocusEvent<HTMLDivElement>): void {
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next)) {
      close(false);
    }
  }

  const grid = monthGrid(focusedDate);
  const focusedIso = toIsoDate(focusedDate);

  return (
    <div className="fo-datepicker" data-invalid={invalid ? '' : undefined}>
      <div className="fo-datepicker__control">
        <input
          id={controlId}
          className="fo-datepicker__input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="YYYY-MM-DD"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          required={required}
          disabled={disabled}
          value={value ?? ''}
          onChange={(event) => {
            const next = event.target.value;
            onChange(next === '' ? null : next);
            const parsed = fromIsoDate(next);
            if (parsed !== null) {
              setFocusedDate(parsed);
            }
          }}
        />
        <button
          ref={triggerRef}
          type="button"
          className="fo-datepicker__trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? dialogId : undefined}
          aria-label={open ? 'Close the calendar' : 'Choose a date from the calendar'}
          disabled={disabled}
          onClick={() => {
            setFocusedDate(selected ?? today(timeZone));
            setOpen((wasOpen) => !wasOpen);
          }}
        >
          <Icon name="calendar" />
        </button>
      </div>

      {open ? (
        <div
          ref={dialogRef}
          className="fo-datepicker__dialog"
          id={dialogId}
          role="dialog"
          aria-modal="false"
          aria-label="Choose a date"
          onKeyDown={handleDialogKeyDown}
          onBlur={handleDialogBlur}
        >
          <div className="fo-datepicker__header">
            <Button
              variant="ghost"
              size="sm"
              icon="chevron-left"
              iconOnly
              onClick={() => {
                setFocusedDate((current) => addMonths(current, -1));
              }}
            >
              Previous month
            </Button>
            <output className="fo-datepicker__month" aria-live="polite">
              {MONTH_LABEL.format(focusedDate)}
            </output>
            <Button
              variant="ghost"
              size="sm"
              icon="chevron-right"
              iconOnly
              onClick={() => {
                setFocusedDate((current) => addMonths(current, 1));
              }}
            >
              Next month
            </Button>
          </div>

          <table
            ref={gridRef}
            className="fo-datepicker__grid"
            role="grid"
            aria-label={MONTH_LABEL.format(focusedDate)}
            onKeyDown={handleGridKeyDown}
          >
            <thead>
              <tr>
                {WEEKDAYS.map((day) => (
                  <th key={day} scope="col" abbr={day}>
                    {day.slice(0, 2)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((week) => (
                <tr key={toIsoDate(week[0] ?? focusedDate)}>
                  {week.map((day) => {
                    const iso = toIsoDate(day);
                    const isFocused = iso === focusedIso;
                    const isSelected = value !== null && iso === value;
                    const outside = day.getUTCMonth() !== focusedDate.getUTCMonth();

                    return (
                      <td key={iso} role="gridcell" aria-selected={isSelected}>
                        <button
                          type="button"
                          className="fo-datepicker__day"
                          data-outside={outside ? '' : undefined}
                          data-selected={isSelected ? '' : undefined}
                          data-focused={isFocused ? '' : undefined}
                          // Roving tabindex: exactly one day is in the tab
                          // order, so Tab leaves the grid instead of walking 42
                          // cells.
                          tabIndex={isFocused ? 0 : -1}
                          aria-label={dayLabel(day)}
                          onClick={() => {
                            choose(day);
                          }}
                        >
                          {day.getUTCDate()}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
